import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@whn/shared';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AuthLayout } from './auth-layout';
import { useRegistrationOpen } from './use-registration';
import { useAuth } from './auth-context';
import {
  AuthDivider,
  GoogleSignInButton,
  googleErrorMessage,
  useGoogleSignIn,
} from './google-sign-in';
import { applyApiError } from './use-form-error';

const FIELDS = ['email', 'password'] as const;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [formError, setFormError] = React.useState('');
  const google = useGoogleSignIn();
  const registration = useRegistrationOpen();

  // Where the user was headed before being bounced to sign-in. Passed to
  // Google so the round trip lands them there rather than on the dashboard.
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
  const googleError = googleErrorMessage(searchParams.get('googleError'));

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError('');
    try {
      await login(values);
      // Return the user to wherever they were headed before the redirect.
      navigate(from ?? '/', { replace: true });
    } catch (error) {
      setFormError(applyApiError(error, setError, FIELDS));
    }
  });

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back. Enter your details to continue."
      footer={
        registration.open ? (
          <>
            New to WHNUNIVERSE?{' '}
            <Link to="/register" className="font-medium text-brand hover:underline">
              Create an organization
            </Link>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {googleError ? <Alert tone="error">{googleError}</Alert> : null}

        {google.available ? (
          <>
            <GoogleSignInButton redirectTo={from} />
            <AuthDivider label="or sign in with email" />
          </>
        ) : null}
      </div>

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {formError ? <Alert tone="error">{formError}</Alert> : null}

        <Field label="Work email" error={errors.email?.message} required>
          {(fieldProps) => (
            <Input
              {...fieldProps}
              {...register('email')}
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="you@businessName.com"
            />
          )}
        </Field>

        <Field label="Password" error={errors.password?.message} required>
          {(fieldProps) => (
            <Input
              {...fieldProps}
              {...register('password')}
              type="password"
              autoComplete="current-password"
            />
          )}
        </Field>

        <Button type="submit" className="w-full" isLoading={isSubmitting}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
